/**
 * layout-tree — F12.N E2.T2b pure tree helpers.
 *
 * Immutable operacje na rekurencyjnym drzewie `LayoutElement[]` (1:1 z SellCall,
 * top-level kształt po E1.T3 migracji). Drzewo zagnieżdża się przez 4 ścieżki:
 *   - `children[]`        — kontenery typu section/card/row/columns/fieldset/panel/...
 *   - `tabs[].children`   — element `type='tabs'`
 *   - `accordionItems[].children` — element `type='accordion'`
 *   - `steps[].children`  — element `type='stepper'` / `'wizard'`
 *
 * Wszystkie funkcje zwracają nowy array (zero mutacji), żeby React reconciler
 * widział zmianę przez referencyjne porównanie.
 *
 * Reuse: typy zduplikowane z `SystemModuleBuilderPage.tsx` (avoid cyclic deps).
 * Po pełnym refactorze T2b builder importuje stąd, a SystemModuleBuilderPage
 * trzyma tylko typ widoku (nie tree).
 */

export interface LayoutElement {
  id: string;
  type: string;
  fieldId?: string;
  /**
   * Ukrycie elementu przez nakładkę per-tenant systemowego layoutu. Dziś ustawiane
   * na polach (`type:'field'`) ikonką oka; `deriveSystemOverrides` czyta tę flagę do
   * `hiddenFieldIds`, a `applyTabOverrides` przywraca ją na odczycie.
   */
  hidden?: boolean;
  settings?: Record<string, unknown>;
  style?: Record<string, unknown>;
  conditionalField?: string | null;
  conditionalOperator?: string | null;
  conditionalValue?: unknown;
  children?: LayoutElement[];
  tabs?: TabItem[];
  accordionItems?: AccordionItem[];
  steps?: Step[];
}

export interface TabItem {
  id: string;
  slug: string;
  title: string;
  icon?: string;
  visibleRoleIds?: string[];
  validateOnChange?: boolean;
  blockOnError?: boolean;
  hidden?: boolean;
  sortOrder?: number;
  children: LayoutElement[];
}

export interface AccordionItem {
  id: string;
  title: string;
  defaultExpanded?: boolean;
  visibleRoleIds?: string[];
  sortOrder?: number;
  children: LayoutElement[];
}

export interface Step {
  id: string;
  title: string;
  icon?: string;
  validation?: boolean;
  sortOrder?: number;
  children: LayoutElement[];
}

/**
 * Znajduje element po `id` rekurencyjnie w `children`, `tabs[].children`,
 * `accordionItems[].children`, `steps[].children`. Zwraca element jako
 * LayoutElement / TabItem / AccordionItem / Step albo null gdy nie znaleziono.
 */
export function findInTree(
  elements: LayoutElement[],
  id: string,
): LayoutElement | TabItem | AccordionItem | Step | null {
  for (const el of elements) {
    if (el.id === id) return el;
    if (el.children) {
      const found = findInTree(el.children, id);
      if (found) return found;
    }
    if (el.tabs) {
      for (const tab of el.tabs) {
        if (tab.id === id) return tab;
        const found = findInTree(tab.children, id);
        if (found) return found;
      }
    }
    if (el.accordionItems) {
      for (const panel of el.accordionItems) {
        if (panel.id === id) return panel;
        const found = findInTree(panel.children, id);
        if (found) return found;
      }
    }
    if (el.steps) {
      for (const step of el.steps) {
        if (step.id === id) return step;
        const found = findInTree(step.children, id);
        if (found) return found;
      }
    }
  }
  return null;
}

/**
 * Usuwa element o `id` z drzewa. Zwraca nowy array bez tego elementu.
 * Jeśli `id` nie istnieje, zwraca PRZYSTOSOWANE kopie drzewa (semantycznie =
 * input, ale referencyjnie nowe).
 */
export function removeFromTree(elements: LayoutElement[], id: string): LayoutElement[] {
  return elements
    .filter((el) => el.id !== id)
    .map((el) => mapDeep(el, (children) => removeFromTree(children, id)));
}

/**
 * Aktualizuje element o `id` przez merge `patch`. Zwraca nowy array.
 * Jeśli `id` nie istnieje, zwraca kopię drzewa bez zmian.
 */
export function updateInTree(
  elements: LayoutElement[],
  id: string,
  patch: Partial<LayoutElement>,
): LayoutElement[] {
  return elements.map((el) => {
    if (el.id === id) {
      return { ...el, ...patch };
    }
    return mapDeep(el, (children) => updateInTree(children, id, patch));
  });
}

/**
 * Wstawia `newElement` jako dziecko kontenera o `containerId`. Jeśli `index`
 * podany, wstawia na konkretnym miejscu (z przesunięciem reszty). Jeśli brak —
 * dorzuca na końcu.
 *
 * `containerId` może wskazywać na:
 *  - LayoutElement z `el.children[]` (sekcja / karta / row / columns / fieldset / panel / ...),
 *  - TabItem z `tab.children[]` (zakładka),
 *  - AccordionItem z `panel.children[]` (panel akordeonu),
 *  - Step z `step.children[]` (krok stepper/wizard).
 *
 * Jeśli `containerId` nie istnieje, zwraca drzewo bez zmian.
 */
export function insertIntoContainer(
  elements: LayoutElement[],
  containerId: string,
  newElement: LayoutElement,
  index?: number,
): LayoutElement[] {
  return elements.map((el) => {
    if (el.id === containerId) {
      return { ...el, children: appendOrInsert(el.children ?? [], newElement, index) };
    }
    let next = el;
    if (el.children) {
      next = {
        ...next,
        children: insertIntoContainer(el.children, containerId, newElement, index),
      };
    }
    if (el.tabs) {
      next = {
        ...next,
        tabs: el.tabs.map((tab) =>
          tab.id === containerId
            ? { ...tab, children: appendOrInsert(tab.children, newElement, index) }
            : {
                ...tab,
                children: insertIntoContainer(tab.children, containerId, newElement, index),
              },
        ),
      };
    }
    if (el.accordionItems) {
      next = {
        ...next,
        accordionItems: el.accordionItems.map((panel) =>
          panel.id === containerId
            ? { ...panel, children: appendOrInsert(panel.children, newElement, index) }
            : {
                ...panel,
                children: insertIntoContainer(panel.children, containerId, newElement, index),
              },
        ),
      };
    }
    if (el.steps) {
      next = {
        ...next,
        steps: el.steps.map((step) =>
          step.id === containerId
            ? { ...step, children: appendOrInsert(step.children, newElement, index) }
            : {
                ...step,
                children: insertIntoContainer(step.children, containerId, newElement, index),
              },
        ),
      };
    }
    return next;
  });
}

function appendOrInsert(
  children: LayoutElement[],
  newElement: LayoutElement,
  index?: number,
): LayoutElement[] {
  return index === undefined
    ? [...children, newElement]
    : [...children.slice(0, index), newElement, ...children.slice(index)];
}

/**
 * Przenosi element o `sourceId` do dziecka kontenera `targetContainerId`.
 * = removeFromTree(source) + insertIntoContainer(target, removed).
 * Jeśli source nie znaleziony lub target nie istnieje, zwraca drzewo bez zmian.
 */
export function moveInTree(
  elements: LayoutElement[],
  sourceId: string,
  targetContainerId: string,
  index?: number,
): LayoutElement[] {
  const sourceElement = findInTree(elements, sourceId);
  if (!sourceElement || !('type' in sourceElement)) return elements;
  // Sprawdź czy target istnieje
  const target = findInTree(elements, targetContainerId);
  if (!target) return elements;
  const withoutSource = removeFromTree(elements, sourceId);
  return insertIntoContainer(withoutSource, targetContainerId, sourceElement, index);
}

/**
 * Reorderuje dzieci kontenera (sekcja / panel akordeonu / zakładka / krok stepper)
 * — przesuwa element o `activeId` na miejsce gdzie znajduje się `overId`. Semantyka
 * `arrayMove` z @dnd-kit/sortable: jeśli `activeId` ma index 2 a `overId` ma index 0,
 * `activeId` ląduje na index 0, reszta jedzie w dół.
 *
 * Używany przez pakiet B Sub-2 (sortowanie w obrębie kontenera). Jeśli `containerId`
 * nie istnieje, albo activeId/overId nie są w tym samym kontenerze, zwraca drzewo
 * bez zmian (referencyjnie nowe — przeszło przez `.map`).
 *
 * Pakiet B Sub-2.
 */
export function reorderInContainer(
  elements: LayoutElement[],
  containerId: string,
  activeId: string,
  overId: string,
): LayoutElement[] {
  const reorderArray = (arr: LayoutElement[]): LayoutElement[] => {
    const oldIdx = arr.findIndex((c) => c.id === activeId);
    const newIdx = arr.findIndex((c) => c.id === overId);
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return arr;
    const result = [...arr];
    const item = result.splice(oldIdx, 1)[0]!;
    result.splice(newIdx, 0, item);
    return result;
  };
  return elements.map((el) => {
    if (el.id === containerId && el.children) {
      return { ...el, children: reorderArray(el.children) };
    }
    let next = el;
    if (el.children) {
      next = { ...next, children: reorderInContainer(el.children, containerId, activeId, overId) };
    }
    if (el.tabs) {
      next = {
        ...next,
        tabs: el.tabs.map((tab) =>
          tab.id === containerId
            ? { ...tab, children: reorderArray(tab.children) }
            : {
                ...tab,
                children: reorderInContainer(tab.children, containerId, activeId, overId),
              },
        ),
      };
    }
    if (el.accordionItems) {
      next = {
        ...next,
        accordionItems: el.accordionItems.map((panel) =>
          panel.id === containerId
            ? { ...panel, children: reorderArray(panel.children) }
            : {
                ...panel,
                children: reorderInContainer(panel.children, containerId, activeId, overId),
              },
        ),
      };
    }
    if (el.steps) {
      next = {
        ...next,
        steps: el.steps.map((step) =>
          step.id === containerId
            ? { ...step, children: reorderArray(step.children) }
            : {
                ...step,
                children: reorderInContainer(step.children, containerId, activeId, overId),
              },
        ),
      };
    }
    return next;
  });
}

/**
 * Reorderuje kroki steppera/wizarda w obrębie elementu typu `stepper` / `wizard` o `stepperId`.
 * Wzorzec identyczny z `reorderAccordionPanels`, operuje na `steps[]`. Etap 3 T2.
 */
export function reorderSteps(
  elements: LayoutElement[],
  stepperId: string,
  activeId: string,
  overId: string,
): LayoutElement[] {
  const reorderArray = (arr: Step[]): Step[] => {
    const oldIdx = arr.findIndex((s) => s.id === activeId);
    const newIdx = arr.findIndex((s) => s.id === overId);
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return arr;
    const result = [...arr];
    const item = result.splice(oldIdx, 1)[0]!;
    result.splice(newIdx, 0, item);
    return result;
  };
  return elements.map((el) => {
    if (el.id === stepperId && el.steps) {
      return { ...el, steps: reorderArray(el.steps) };
    }
    return mapDeep(el, (children) => reorderSteps(children, stepperId, activeId, overId));
  });
}

/**
 * Reorderuje panele akordeonu w obrębie elementu typu `accordion` o `accordionId`.
 * Wzorzec identyczny z `reorderInContainer`, ale operuje na `accordionItems[]` zamiast
 * `children[]`. Pakiet B Sub-2.
 */
export function reorderAccordionPanels(
  elements: LayoutElement[],
  accordionId: string,
  activeId: string,
  overId: string,
): LayoutElement[] {
  const reorderArray = (arr: AccordionItem[]): AccordionItem[] => {
    const oldIdx = arr.findIndex((p) => p.id === activeId);
    const newIdx = arr.findIndex((p) => p.id === overId);
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return arr;
    const result = [...arr];
    const item = result.splice(oldIdx, 1)[0]!;
    result.splice(newIdx, 0, item);
    return result;
  };
  return elements.map((el) => {
    if (el.id === accordionId && el.accordionItems) {
      return { ...el, accordionItems: reorderArray(el.accordionItems) };
    }
    // Rekurencja w głąb (akordeony zagnieżdżone w sekcji / zakładce / kroku).
    return mapDeep(el, (children) =>
      reorderAccordionPanels(children, accordionId, activeId, overId),
    );
  });
}

/**
 * Najmniejszy kształt węzła drzewa układu, po którym chodzą funkcje o typach elementów
 * (`collectElementTypes`, `removeElementsOfTypes`, `isLayoutSingletonTaken`): `type` + dzieci w
 * `children` / `tabs[].children` / `accordionItems[].children` / `steps[].children`. Funkcje są
 * generyczne po węźle (`T extends LayoutTreeNode<T>`), więc przyjmują zarówno `LayoutElement` stąd,
 * jak i własny typ elementu strony (kreator) bez rzutowania.
 */
export interface LayoutTreeNode<T> {
  type: string;
  children?: T[];
  tabs?: ReadonlyArray<{ children: T[] }>;
  accordionItems?: ReadonlyArray<{ children: T[] }>;
  steps?: ReadonlyArray<{ children: T[] }>;
}

/**
 * Zbiór typów elementów obecnych W CAŁYM drzewie (`children`, `tabs[].children`,
 * `accordionItems[].children`, `steps[].children` — dowolna głębokość). Do wykrywania
 * typów-singletonów layoutu (`LayoutElementDefinition.layoutSingleton`).
 */
export function collectElementTypes<T extends LayoutTreeNode<T>>(
  elements: readonly T[],
): Set<string> {
  const types = new Set<string>();
  const walk = (list: readonly T[]): void => {
    for (const el of list) {
      types.add(el.type);
      // Wspólny przebieg po listach dzieci (`mapDeep`) — jako odwiedzający, wynik odrzucany.
      mapDeep(el, (children) => {
        walk(children);
        return children;
      });
    }
  };
  walk(elements);
  return types;
}

/**
 * Usuwa z drzewa WSZYSTKIE elementy podanych typów (dowolna głębokość). Zwraca nowy array.
 * Używane przy klonowaniu fragmentu layoutu, żeby kopia nie powieliła typu-singletona.
 */
export function removeElementsOfTypes<T extends LayoutTreeNode<T>>(
  elements: readonly T[],
  types: ReadonlySet<string>,
): T[] {
  return elements
    .filter((el) => !types.has(el.type))
    .map((el) => mapDeep(el, (children) => removeElementsOfTypes(children, types)));
}

/**
 * Czy w drzewie jest jakikolwiek element, który NIE jest kontenerem (`isContainerType` — zwykle
 * flaga `isContainer` z rejestru elementów): pole, blok treści itp., na dowolnej głębokości.
 * Sama pusta skorupa z kontenerów (sekcja w zakładce w akordeonie, bez niczego w środku) to
 * `false` — taka zawartość nie niesie nic do skopiowania.
 */
export function hasNonContainerElement<T extends LayoutTreeNode<T>>(
  elements: readonly T[],
  isContainerType: (type: string) => boolean,
): boolean {
  let found = false;
  const walk = (list: readonly T[]): void => {
    for (const el of list) {
      if (found) return;
      if (!isContainerType(el.type)) {
        found = true;
        return;
      }
      mapDeep(el, (children) => {
        walk(children);
        return children;
      });
    }
  };
  walk(elements);
  return found;
}

/**
 * Czy dodanie kolejnego elementu typu `type` złamałoby regułę „jeden blok na layout”: typ jest
 * singletonem (`isSingletonType` — zwykle flaga `layoutSingleton` z rejestru elementów) i występuje
 * już GDZIEKOLWIEK w drzewie. Czysta decyzja bez toastów i stanu — woła ją kreator przy dropie,
 * kliknięciu w paletę i duplikowaniu sekcji.
 */
export function isLayoutSingletonTaken<T extends LayoutTreeNode<T>>(
  elements: readonly T[],
  type: string,
  isSingletonType: (type: string) => boolean,
): boolean {
  return isSingletonType(type) && collectElementTypes(elements).has(type);
}

/**
 * Helper: wywołuje `fn` na każdym `children` / `tabs[].children` /
 * `accordionItems[].children` / `steps[].children` elementu i zwraca element
 * z odpowiednim polem zaktualizowanym. Jedyny przebieg po wszystkich czterech
 * ścieżkach zagnieżdżenia — używany przez operacje rekurencyjne (remove/update/insert/
 * move/collect) żeby propagować zmianę w głąb. Generyczny po `LayoutTreeNode`, więc działa
 * też na własnym typie elementu strony (kreator) bez rzutowania.
 */
function mapDeep<T extends LayoutTreeNode<T>>(element: T, fn: (children: T[]) => T[]): T {
  let next = element;
  if (element.children) {
    next = { ...next, children: fn(element.children) };
  }
  if (element.tabs) {
    next = { ...next, tabs: element.tabs.map((tab) => ({ ...tab, children: fn(tab.children) })) };
  }
  if (element.accordionItems) {
    next = {
      ...next,
      accordionItems: element.accordionItems.map((panel) => ({
        ...panel,
        children: fn(panel.children),
      })),
    };
  }
  if (element.steps) {
    next = {
      ...next,
      steps: element.steps.map((step) => ({ ...step, children: fn(step.children) })),
    };
  }
  return next;
}
